import {tokens} from '@commercial-helios/ui/theme'
import React from 'react'
import styled, {css} from 'styled-components'

import type {Props as CardProps} from '@/components/card'
import Card from '@/components/card'
import type {BackgroundColor} from '@/utils/is-valid-background-color'
import Dropdown from '@helios/shared/components/dropdown'
import type {Props as RichTextProps} from '@helios/shared/components/rich-text'
import RichText, {hasRichText} from '@helios/shared/components/rich-text'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import type {LayoutProps} from '@helios/shared/styles/grid'
import {Section, Layout, BackgroundWrapper} from '@helios/shared/styles/grid'
import {Text, TextLink} from '@helios/shared/styles/typography'
import mq from '@helios/shared/utils/media-query'

type CardGridCard = CardProps & {
    filterKeywords?: string[] | null
    cta_link?: CardProps['ctaLink']
}

export interface Props {
    title?: RichTextProps['content']
    subtitle?: string
    cards: CardGridCard[]
    showMoreText: string
    initialCardsShownOnMobile?: 4 | 8 | 12 | 16 | 20
    initialCardsShownOnDesktop?: 4 | 8 | 12 | 16 | 20
    showFilter?: boolean
    layout?: LayoutProps['$variant']
    dataTestId?: string
    backgroundColor?: BackgroundColor
    withPadding?: boolean
}

const CardGrid = React.forwardRef<HTMLDivElement, Props>(
    (
        {
            title,
            subtitle,
            cards,
            showMoreText,
            initialCardsShownOnMobile,
            initialCardsShownOnDesktop,
            showFilter,
            layout = 'four-equal',
            dataTestId,
            backgroundColor = 'grey',
            withPadding = true,
        },
        ref,
    ) => {
        // We want to make sure this value doesn't collide with any user-provided filter values
        const allCardsFilterValue = React.useId()
        const [selectedOption, setSelectedOption] = React.useState(allCardsFilterValue)
        const {show_me, all} = useGlobalStrings()

        const filterCard = (card: CardGridCard) =>
            !card.filterKeywords || card.filterKeywords?.includes(selectedOption)

        const filteredCards =
            selectedOption === allCardsFilterValue ? cards : cards.filter(filterCard)

        const [mobileCardsLimit, setMobileCardsLimit] = React.useState<number>(
            typeof initialCardsShownOnMobile === 'number' ? initialCardsShownOnMobile : Infinity,
        )
        const [desktopCardsLimit, setDesktopCardsLimit] = React.useState(
            typeof initialCardsShownOnDesktop === 'number' ? initialCardsShownOnDesktop : Infinity,
        )

        const handleShowMore = () => {
            setMobileCardsLimit(Infinity)
            setDesktopCardsLimit(Infinity)
        }

        const uniqueFilterKeywords = Array.from(
            new Set(
                cards
                    .filter((card) => !!card.filterKeywords)
                    .flatMap((card) => card.filterKeywords!),
            ),
        )

        const dropdownOptions = [
            {
                value: allCardsFilterValue,
                label: all,
            },
            ...uniqueFilterKeywords.sort().map((keyword) => ({
                value: keyword,
                label: keyword,
            })),
        ]

        return (
            <BackgroundWrapper ref={ref} $color={backgroundColor}>
                <Wrapper
                    $mobileLimit={mobileCardsLimit}
                    $desktopLimit={desktopCardsLimit}
                    $totalCards={filteredCards.length}
                    data-testid={dataTestId}
                >
                    {hasRichText(title) && (
                        <TitleSubtitleWrapper>
                            <RichText variant="heading-large" content={title} />

                            {subtitle && (
                                <Text variant="title-base" color="muted">
                                    {subtitle}
                                </Text>
                            )}
                        </TitleSubtitleWrapper>
                    )}

                    {showFilter && (
                        <Dropdown
                            triggerText={show_me}
                            options={dropdownOptions}
                            value={selectedOption}
                            onChange={setSelectedOption}
                        />
                    )}

                    <CardGridWrapper
                        {...(dataTestId && {'data-testid': `${dataTestId}-card-grid-wrapper`})}
                    >
                        <Layout $variant={layout} $alignItems="stretch">
                            {filteredCards.map((card, i) => (
                                <StyledCard
                                    {...card}
                                    key={`${card.title}-${i}`}
                                    ctaLink={card.cta_link}
                                    dataTestId={`${card.title}-${i}`}
                                    $withPadding={withPadding}
                                />
                            ))}
                        </Layout>

                        <StyledTextLink
                            as="button"
                            onClick={handleShowMore}
                            {...(dataTestId && {'data-testid': `${dataTestId}-show-more-button`})}
                        >
                            {showMoreText}
                        </StyledTextLink>
                    </CardGridWrapper>
                </Wrapper>
            </BackgroundWrapper>
        )
    },
)

const CardGridWrapper = styled.div`
    display: grid;
`

const StyledCard = styled(Card)<{$withPadding: boolean}>`
    ${(props) =>
        !props.$withPadding &&
        css`
            padding: 0;
        `}
`

const StyledTextLink = styled(TextLink)`
    margin-top: ${tokens.spacing24};
    justify-self: center;

    ${mq.min('md')} {
        margin-top: ${tokens.spacing40};
    }

    ${mq.min('xl')} {
        margin-top: ${tokens.spacing20};
    }
`

interface WrapperProps {
    $mobileLimit: number
    $desktopLimit: number
    $totalCards: number
}

const mobileBreakpoint = 'sm'

const hide = css`
    display: none;
`

const Wrapper = styled(Section)<WrapperProps>`
    display: grid;
    row-gap: ${tokens.spacing60};

    ${mq.max(mobileBreakpoint)} {
        ${StyledTextLink} {
            ${(props) => props.$mobileLimit >= props.$totalCards && hide}
        }

        ${StyledCard}:nth-child(n + ${(props) => props.$mobileLimit + 1}) {
            ${hide}
        }
    }

    ${mq.min(mobileBreakpoint)} {
        ${StyledTextLink} {
            ${(props) => props.$desktopLimit >= props.$totalCards && hide}
        }

        ${StyledCard}:nth-child(n + ${(props) => props.$desktopLimit + 1}) {
            ${hide}
        }
    }
`

const TitleSubtitleWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing20};
    max-width: 540px;
`

export default CardGrid
